import React, { useMemo, useRef } from "react";
import {
  GET_GROUP_BY,
  SET_SELECTED_GROUP,
  DEFAULT,
  securityLegend,
  visibilityLegend,
  SET_SITEMAP_EXPAND_ACCORDION,
  secondDropdownValues,
  GET_SECOND_TAGS_DROPDOWN,
  USER_TAGS,
  SYSTEM_TAGS,
  defaultPaginationModel,
  GET_SUB_TAG_DATA,
  GET_GROUP_BY_DATA,
  severityColorMap,
  desiredOrderCVESeverity,
  alertsOrderGroups,
  SET_SELECTED_LABEL,
  GET_CVE_LIST_BY_SEVERITY,
  sensitiveGroups,
  severityLightColorMap,
  SET_NETSPACE_LOADER,
} from "./NetSpaceConstant";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { MenuItem, Select, useMediaQuery } from "@material-ui/core";
import { formatNumberUS, NetSpaceSecondDropdown, renderIsMonitored } from "./NetSpaceUtils";
import Chip from "@material-ui/core/Chip";
import { startCase, toLower } from "lodash";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import LazyAccordion from "./LazyAccordion";
const LazyNetSpaceTable = React.lazy(() => import("./NetSpaceTable"));

const ControlPanel = () => {
  const dispatch = useDispatch();
  const isSmall = useMediaQuery("(max-width:1600px)");
  const isMediumQuery = useMediaQuery("(max-width:2000px)");
  const groups = useSelector((state) => state.NetspaceReducer.groups);
  const selectedGroup = useSelector(
    (state) => state.NetspaceReducer.selectedGroup,
  );
  const fetchedRef = useRef({});
  const groupCounts = useSelector((state) => state.NetspaceReducer.groupCount);
  const accordionData = useSelector(
    (state) => state.NetspaceReducer.accordionData,
  );
  const expandAccordionState = useSelector(
    (state) => state.NetspaceReducer.expandAccordion,
  );
  const reduxSelectedLabel = useSelector(
    (state) => state.NetspaceReducer?.selectedLabel,
  );
  const cveDataMap = useSelector(
    (state) => state.NetspaceReducer.cveAccordionData,
  );
  const paginationModel =
    accordionData && accordionData.paginationModel
      ? accordionData.paginationModel
      : defaultPaginationModel;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [secondGroupCode, setSecondGroupCode] = useState(null);
  const [selectedSubGroup, setSelectedSubGroup] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const selectedLabel = reduxSelectedLabel;
  const [selectedVisibility, setSelectedVisibility] = useState(null);
  const [pendingExpand, setPendingExpand] = useState(null);
  const defaultVisibility = {
    Type: false,
    Ports: false,
    "User Tags": false,
  };
  const isAnyAccordionOpen = useMemo(() => {
    return Object.values(expandAccordionState || {}).some(Boolean);
  }, [expandAccordionState]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const saved = localStorage.getItem("netspace-columns");

    if (saved) {
      return {
        ...defaultVisibility,
        ...JSON.parse(saved),
      };
    }

    return defaultVisibility;
  });

  useEffect(() => {
    if (!isAnyAccordionOpen) {
      setSelectedVisibility(null);

      // IMPORTANT: clear redux filter too
      dispatch({
        type: SET_SELECTED_LABEL,
        payload: null,
      });
    }
  }, [isAnyAccordionOpen]);

  useEffect(() => {
    localStorage.setItem(
      "netspace-columns",
      JSON.stringify(columnVisibilityModel),
    );
  }, [columnVisibilityModel]);

  useEffect(() => {
    dispatch({
      type: GET_GROUP_BY,
    });
  }, []);

  const groupCount = useMemo(() => {
    //Custom severity order
    if (alertsOrderGroups.includes(selectedGroup)) {
      return Object.fromEntries(
        desiredOrderCVESeverity
          .filter((key) => groupCounts[key] !== undefined)
          .map((key) => [key, groupCounts[key]]),
      );
    }

    // Sort by count (VULNERABILITY_ID)
    if (selectedGroup === "VULNERABILITY_ID") {
      return Object.fromEntries(
        Object.entries(groupCounts || {}).sort((a, b) => b[1] - a[1]), // highest first
      );
    }

    // Default
    return groupCounts;
  }, [selectedGroup, groupCounts]);

  useEffect(() => {
    if (selectedGroup === "USER_TAGS") {
      dispatch({
        type: GET_SECOND_TAGS_DROPDOWN,
        payload: { selectedTag: USER_TAGS },
      });
    }
    if (selectedGroup === "SYSTEM_TAGS") {
      dispatch({
        type: GET_SECOND_TAGS_DROPDOWN,
        payload: { selectedTag: SYSTEM_TAGS },
      });
    }
    if (secondDropdownValues.includes(selectedGroup)) {
      setOpen(true);
    } else {
      setOpen(false);
    }
    dispatch({
      type: SET_SITEMAP_EXPAND_ACCORDION,
      payload: {},
    });
  }, [selectedGroup]);

  useEffect(() => {
    if (!pendingExpand || !groupCount || Object.keys(groupCount).length === 0) {
      return;
    }

    const normalizedKey = Object.keys(groupCount).find(
      (key) => key.toLowerCase() === pendingExpand.toLowerCase(),
    );

    if (!normalizedKey) return;

    const newExpandState = {};

    Object.keys(groupCount).forEach((key) => {
      newExpandState[key] = key === normalizedKey;
    });

    dispatch({
      type: SET_SITEMAP_EXPAND_ACCORDION,
      payload: newExpandState,
    });

    handleAccordionExpand(normalizedKey);

    setPendingExpand(null);
  }, [groupCount, pendingExpand]);

  useEffect(() => {
    if (reduxSelectedLabel) {
      setPendingExpand(reduxSelectedLabel);
    }
  }, [reduxSelectedLabel]);

  useEffect(() => {
    if (!expandAccordionState) return;

    const anyExpanded = Object.values(expandAccordionState).some(Boolean);

    if (!anyExpanded) {
      dispatch({
        type: SET_SELECTED_LABEL,
        payload: null,
      });
    }
  }, [expandAccordionState]);

  useEffect(() => {
    fetchedRef.current = {};
    if (selectedGroup === DEFAULT) {
      dispatch({
        type: GET_GROUP_BY_DATA,
        payload: {
          key: DEFAULT,
          values: [""],
          pageNo: 0,
          pageSize: 20,
        },
      });
    }
  }, [selectedGroup]);

  const fetchAccordionData = (accordionName) => {
    const existing = accordionData?.[accordionName];

    // ONLY block real in-flight request
    if (existing?.loading) return;

    if (secondDropdownValues.includes(selectedGroup)) {
      dispatch({
        type: GET_SUB_TAG_DATA,
        payload: {
          selectedTag: selectedGroup,
          secondTag: selectedSubGroup,
          data: [{ value: accordionName }],
          page: paginationModel.page,
          size: paginationModel.pageSize,
        },
      });
      return;
    }

    if (selectedGroup === "VULNERABILITIES") {
      dispatch({
        type: GET_CVE_LIST_BY_SEVERITY,
        payload: { severity: accordionName },
      });
      return;
    }

    dispatch({
      type: GET_GROUP_BY_DATA,
      payload: {
        key: selectedGroup,
        values: selectedGroup === DEFAULT ? [""] : [accordionName],
        pageNo: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
    });
  };

 const handleAccordionExpand = (accordionName) => {
   const isExpanding = !expandAccordionState[accordionName];

   dispatch({
     type: SET_SITEMAP_EXPAND_ACCORDION,
     payload: {
       ...expandAccordionState,
       [accordionName]: isExpanding,
     },
   });

   if (isExpanding) {
     fetchAccordionData(accordionName); // ✅ USE IT HERE
   }
 };

  const renderSideArrow = (group) => {
    const isSecondTagGroup = secondDropdownValues.includes(group.code);

    const handleClick = () => {
      dispatch({
        type: SET_SELECTED_GROUP,
        payload: { group: group.code, place: "non-widget" },
      });
    };

    const handleChevronClick = (e) => {
      e.stopPropagation(); // prevent parent click
      setSecondGroupCode(group.code);
      setAnchorEl(e.currentTarget); // anchor for dropdown
    };

    return (
      <MenuItem value={group.code} key={group.code} onClick={handleClick}>
        <div className="netspace-group-menu-item">
          <div className="netspace-group-menu-label">{group.value}</div>
          {isSecondTagGroup && (
            <ChevronRightIcon
              onClick={handleChevronClick}
              style={{ cursor: "pointer" }}
            />
          )}
        </div>
      </MenuItem>
    );
  };

  const topThree = Object.entries(groupCount || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const totalGroups = Object.keys(groupCount || {}).length;

  const renderDropdownValue = () => {
    if (selectedGroup === DEFAULT) {
      return (
        <div className="header-count-item static">
          <div className="header-count-item-label">
            {startCase(toLower(DEFAULT))}
          </div>
          <div className="header-count-item-value">
            <Chip label={formatNumberUS(accordionData[""]?.data?.totalElements || 0)} />
          </div>
        </div>
      );
    }

    return topThree.map(([key, count]) => {
      // pick color based on the actual key (ALERTS / VULNERABILITIES / VULNERABLE_HOSTS)
      const groupColor = severityColorMap[key] || "transparent";
      const sensitiveGroupColor = severityLightColorMap[key] || "transparent"

      const isSensitive = sensitiveGroups.includes(selectedGroup);

      return (
        <div
          key={key}
          className="header-count-item static"
          style={
            isSensitive
              ? {
                  border: `2px solid ${groupColor}`,
                  backgroundColor: `${sensitiveGroupColor}`,
                  color: "black"
                }
              : {}
          }
        >
          <div className="header-count-item-label">
            {key ? startCase(toLower(key)) : "NA"}
          </div>

          <div className="header-count-item-value">
            <Chip label={formatNumberUS(count)} />
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <div className="control-panel">
        {groups && groups.length > 0 && (
          <div className="netspace-selection">
            <span className="selection-lbl">Group By</span>

            <div className="groupby-wrapper">
              <Select
                labelId="table-select-label"
                id="group-by-select"
                value={selectedGroup}
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => setSelectOpen(false)}
                disableUnderline
                className="groupby-select"
                renderValue={(value) => {
                  const mainGroup = startCase(toLower(value));
                  const subGroup = selectedSubGroup
                    ? ` → ${selectedSubGroup}`
                    : "";
                  return !secondDropdownValues.includes(selectedGroup)
                    ? mainGroup
                    : `${mainGroup}${subGroup}`;
                }}
              >
                {groups?.map((group) => renderSideArrow(group))}
              </Select>
            </div>
          </div>
        )}
        <div className="topData">
          <div className="header-total-host">
            {selectedGroup === DEFAULT
              ? "Total Hosts"
              : totalGroups > 3
                ? "Top 3 Hosts:"
                : "Total Hosts:"}
          </div>
          <div className="header-counts">{renderDropdownValue()}</div>
        </div>
        <div className="header-legend visibility">
          <span className="header-total-host">Visibility:</span>

          {visibilityLegend.map((s) => {
            const isActive = selectedVisibility === s.isMonitored;

            return (
              <span
                key={s.isMonitored}
                className={`visibility-icon ${s.class} ${
                  isActive ? "active" : ""
                }`}
                title={s.label}
                onClick={() => {
                  setSelectedVisibility((prev) =>
                    prev === s.isMonitored ? null : s.isMonitored,
                  );
                  const isSameLabel = reduxSelectedLabel === s.isMonitored;
                  dispatch({ type: SET_NETSPACE_LOADER, payload: true })
                  dispatch({
                    type: SET_SITEMAP_EXPAND_ACCORDION,
                    payload: {},
                  });

                  if (isSameLabel) {
                    dispatch({
                      type: SET_SELECTED_LABEL,
                      payload: null,
                    });

                    dispatch({
                      type: SET_SITEMAP_EXPAND_ACCORDION,
                      payload: {},
                    });

                    return;
                  }
                  dispatch({
                    type: SET_SELECTED_LABEL,
                    payload:  s.isMonitored,
                  });
                  if (selectedGroup !== "MONITORING") {
                    dispatch({
                      type: SET_SELECTED_GROUP,
                      payload: {
                        group: "MONITORING",
                        place: "non-widget",
                      },
                    });
                  }
                }}
              >
                {renderIsMonitored({
                  row: { isMonitored: s.isMonitored },
                  isActive,
                })}
              </span>
            );
          })}
        </div>
      </div>
      <div className="netspace-tbl-container">
        {selectedGroup === DEFAULT ? (
          <LazyAccordion
            label={startCase(toLower(DEFAULT))}
            count={formatNumberUS(accordionData[""]?.data?.totalElements || 0)}
            groupKey=""
            isExpanded={expandAccordionState[""] || false}
            onToggle={handleAccordionExpand}
            LazyComponent={LazyNetSpaceTable}
            selectedSubGroup={selectedSubGroup}
            lazyProps={{
              columnVisibilityModel,
              setColumnVisibilityModel,
            }}
          />
        ) : (
          Object.keys(groupCount).map((key) => {
            const isVulnGroup = selectedGroup === "VULNERABILITIES";

            const cveDataObj = cveDataMap?.[key]?.cveMap || {};
            const cveEntries = Object.entries(cveDataObj);

            return (
              <LazyAccordion
                key={key}
                label={startCase(toLower(key))}
                count={formatNumberUS(groupCount[key])}
                groupKey={key}
                isExpanded={expandAccordionState[key] || false}
                onToggle={handleAccordionExpand}
                selectedSubGroup={selectedSubGroup}
                LazyComponent={
                  isVulnGroup
                    ? () => {
                        if (!expandAccordionState[key]) return null;

                        // loading state
                        if (!cveEntries.length) {
                          return <div style={{ padding: 12 }}>Loading...</div>;
                        }

                        return (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              width: "100%",
                            }}
                          >
                            {cveEntries.map(([cveId, count]) => (
                              <LazyAccordion
                                key={cveId}
                                label={cveId}
                                count={formatNumberUS(count)}
                                groupKey={cveId}
                                isExpanded={
                                  expandAccordionState[cveId] || false
                                }
                                onToggle={(cveId) => {
                                  const isExpanding =
                                    !expandAccordionState[cveId];

                                  dispatch({
                                    type: SET_SITEMAP_EXPAND_ACCORDION,
                                    payload: {
                                      ...expandAccordionState,
                                      [cveId]: isExpanding,
                                    },
                                  });

                                  if (!isExpanding) return;

                                  if (fetchedRef.current[cveId]) return;

                                  fetchedRef.current[cveId] = true;

                                  dispatch({
                                    type: GET_GROUP_BY_DATA,
                                    payload: {
                                      key: "VULNERABILITY_ID",
                                      values: [cveId],
                                      pageNo: paginationModel.page,
                                      pageSize: paginationModel.pageSize,
                                    },
                                  });
                                }}
                                LazyComponent={LazyNetSpaceTable}
                                lazyProps={{
                                  columnVisibilityModel,
                                  setColumnVisibilityModel,
                                  parentSeverity: key,
                                  isSecondLevelCve: true,
                                }}
                              />
                            ))}
                          </div>
                        );
                      }
                    : LazyNetSpaceTable
                }
                lazyProps={{
                  columnVisibilityModel,
                  setColumnVisibilityModel,
                }}
              />
            );
          })
        )}
      </div>

      <div className="netspace-second-dropdown-container">
        <NetSpaceSecondDropdown
          anchorEl={anchorEl}
          loc={secondGroupCode}
          onClose={() => {
            setSelectOpen(false);
            setAnchorEl(null);
          }}
          onSelect={(subGroupCode) => setSelectedSubGroup(subGroupCode)}
        />
      </div>
    </>
  );
};

export default ControlPanel;
